/*
 * Step 1: Generate an Angular library for auth
 *    ng generate library auth
 * 
 * Step 2: Inside projects/auth/src/lib, create a service file:
 *    keycloak-adapter.service.ts
 */

import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import type {
  KeycloakConfig,
  KeycloakInitOptions,
  KeycloakProfile,
  KeycloakTokenParsed
} from 'keycloak-js';

@Injectable({ providedIn: 'root' })
export class KeycloakAuthorizationService {
  private keycloakInstance!: Keycloak;

  /**
   * Initialize Keycloak instance with config and init options
   */
  init(config: KeycloakConfig, options: KeycloakInitOptions): Promise<void> {
    this.keycloakInstance = new Keycloak(config);
    return this.keycloakInstance.init(options)
      .then(authenticated => {
        if (!authenticated) {
          console.warn('User not authenticated');
          return; //Promise.reject('Keycloak authentication failed');
        }
        this.setupTokenRefresh();
        return; //Promise.resolve();
      });
  }

  /**
   * Trigger login redirect
   */
  login(): void {
    this.keycloakInstance.login();
  }

  /**
   * Trigger logout redirect
   */
  logout(): void {
    this.keycloakInstance.logout();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.keycloakInstance && this.keycloakInstance.authenticated === true;
  }

  /**
   * Get current token, refreshing if needed
   */
  async getToken(): Promise<string | undefined> {
    if (!this.keycloakInstance) return undefined;

    try {
      await this.keycloakInstance.updateToken(60); // refresh if expiring in 60s
      return this.keycloakInstance.token;
    } catch (e) {
      this.login(); // fallback to login
      return undefined;
    }
  }


  /**
   * Load user profile
   */
  loadUserProfile(): Promise<KeycloakProfile> {
    return this.keycloakInstance.loadUserProfile();
  }
  /**
   * Explicitly update the token with your desired minimum validity (in seconds).
   * Returns a promise that resolves to true if the token is still valid,
   * or rejects if it couldn’t refresh (so you can call login()).
   */
  updateToken(minValidity: number): Promise<boolean> {
    return this.keycloakInstance.updateToken(minValidity);
  }

  get tokenParsed(): KeycloakTokenParsed | undefined {
    return this.keycloakInstance?.tokenParsed;
  }

  accountManagement(): Promise<void> {
    return this.keycloakInstance.accountManagement();
  }

  /**
   * Set up automatic token refresh before expiry
   */
  private setupTokenRefresh(): void {
    setInterval(() => {
      this.keycloakInstance.updateToken(60)
        .catch(() => this.keycloakInstance.login());
    }, 60000); // every minute
  }
}
